import React, { ComponentType, useCallback } from "react";

import Located from "@mapmycustomers/shared/types/base/Located";
import { EntityWithLocation } from "@mapmycustomers/shared/types/entity";
import { GeoPoint } from "@mapmycustomers/shared/types/shapes";
import { isDefined, isMapped } from "@mapmycustomers/shared/util/assert";

import RecordLocationMiniMap from "@app/component/field/LocationMiniMap/RecordLocationMiniMap";
import GeoLocationInfo from "@app/component/preview/components/GeoLocationInfo";
import MapLink from "@app/component/preview/components/MapLink";
import MapPopup from "@app/component/preview/components/MapPopup";
import LocationViewMode from "@app/component/preview/enum/LocationViewMode";
import { EntityWithLocationProps } from "@app/types/map/EntityWithLocationProps";
import getFieldModelByEntityType from "@app/util/fieldModel/getByEntityType";

import styles from "./Location.module.scss";
import Territories from "./Territories";

interface Props {
  addressLoading?: boolean;
  entity: EntityWithLocation;
  formChanged: boolean;
  isMapMode: boolean;
  isReadOnlyMember?: boolean;
  locationEditComponent?: ComponentType<EntityWithLocationProps>;
  onAddressChange?: (updated: Located) => void;
}

const Location: React.FC<Props> = ({
  addressLoading,
  entity,
  formChanged,
  isMapMode,
  isReadOnlyMember,
  locationEditComponent: LocationEditComponent,
  onAddressChange,
}) => {
  const hasLocation = isMapped(entity);

  const locationViewMode =
    hasLocation && !entity.address && !entity.region && !entity.postalCode
      ? LocationViewMode.COORDINATES
      : LocationViewMode.ADDRESS;

  const isLocationMode = locationViewMode === LocationViewMode.COORDINATES;
  const [lon, lat] = hasLocation ? entity.geoPoint.coordinates : [];
  const areCoordinatesValid = isDefined(lat) && !isNaN(lat) && isDefined(lon) && !isNaN(lon);
  const mapPreviewRecord = isLocationMode
    ? areCoordinatesValid
      ? {
          ...entity,
          geoPoint: { coordinates: [lon, lat], type: "Point" } as GeoPoint,
        }
      : undefined
    : entity;

  const handleLocationChange = useCallback(
    (located: Located) => onAddressChange?.(located),
    [onAddressChange]
  );

  const handlePinLocationUpdate = useCallback(
    (updatedEntity: EntityWithLocation) => onAddressChange?.(updatedEntity),
    [onAddressChange]
  );

  const addressField = getFieldModelByEntityType(entity.entity)?.getByName("address");
  const isAddressReadable = addressField?.isReadable ?? false;
  const isAddressEditable = (addressField?.isEditable ?? false) && !isReadOnlyMember;

  return (
    <section className={styles.container}>
      <div className={styles.location}>
        {!isMapMode && isAddressReadable && (
          <div>
            {hasLocation && <RecordLocationMiniMap className={styles.map} entity={entity} />}
          </div>
        )}
        {isAddressReadable && (
          <div className={styles.addressInformation}>
            {LocationEditComponent ? (
              <LocationEditComponent
                entity={mapPreviewRecord!}
                onUpdate={handlePinLocationUpdate}
              />
            ) : (
              <>
                <GeoLocationInfo entity={entity} />
                <div>
                  <MapPopup
                    changeButtonDisabled={formChanged || !isAddressEditable}
                    changeButtonLoading={addressLoading}
                    entity={mapPreviewRecord}
                    onUpdate={handleLocationChange}
                    useButton
                  />
                </div>
              </>
            )}
          </div>
        )}
        <Territories territories={entity.territories} />
      </div>
      {!isMapMode && isAddressReadable && hasLocation && <MapLink entity={entity} />}
    </section>
  );
};

export default Location;
